import { CollectionViewer, ListRange } from '@angular/cdk/collections';
import { DataSource } from '@angular/cdk/table';
import { BehaviorSubject, Observable } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';
import { TreeItemWrapper } from './../../components/controls/wiz-tree/tree-item.wrapper';
import { WDSChanges, WDSFilterFunction, WDSSelectionMode, WDSSortField, WDSSortFieldWay, WizCollectionUtils, WizStringUtils } from '@wizeoo/akm-core-lib';

/**
 * Classe définissant une source de donnée utilisée dans tous les composants affichant
 * ou utlisant une liste d'entités.
 *
 * Grâce à la WizDatasource, un seul point d'entré permet de synchroniser la datasource
 * et toutes les opérations effectuées sur celle-ci.
 *
 * La collection d'origine donnée à la construction de la datasource reste inchangée
 * La WizDatasource clone la collection d'origine pour assurer l'idempotence
 *
 * La WizDatasource utilise les types génériques pour typer les objets utilisés dans la datasource
 *
 * Pour faciliter son utilisation, la WizDatasource est iterable
 *
 * La WizDatasource permet entre autre d'appliquer des filtres, des tris et de gérer la sélection des élements
 * à un seul et même endroit. L'utilisation des Observable facilite également la communication entre tous les élements
 * liés à une datasource
 */
export class WizDatasource<T extends any> extends DataSource<T> implements Iterable<T> {
  protected readonly _renderedData: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);

  /** La source de données d'origine clonée lors du build */
  protected _source: T[];
  /**
   * La source de données travaillées sur laquelle les filtres et les tris sont appliqués
   * passage en any pour factorisation avec hierarchical qui encapsule en TIW
   */
  protected _workedSource: any[];
  /** La filter function active */
  protected _filterFuncs: WDSFilterFunction<T>[];
  /** Les champs de tri de la collection */
  protected _sortFields: WDSSortField[];
  /** Fonction de tri personnalisée */
  protected _sortFn: (pItemA: T, pItemB: T) => number;
  /** Observable utilisé pour transmettre les changements sur le tri de la datasource */
  protected _sortChange: BehaviorSubject<WDSSortField[]>;
  protected _sortChanged: boolean = false;
  protected _ignoreSort: boolean = false;
  /** Le mode de sélection */
  protected _selectionMode: WDSSelectionMode = 'single';

  /** Les items sélectionnés */
  protected _selectedItems: T[] = [];

  /** Indique si la datasource a été finalisée (construite). C'est un événement unique */
  protected _finalized: boolean = false;
  /** Sujet d'écoute des changements sur la datasource */
  protected _changes: BehaviorSubject<WDSChanges>;
  /** Le BehaviorSubject indiquant la nécessité de reconstruire la datasource */
  protected _refreshSubject: BehaviorSubject<void>;

  /** construction sur une collection / la collection resultant d'un observable (appel remote) */
  constructor(pSource?: T[] | Observable<T[]>) {
    super();

    if (!!pSource) {
      if (Array.isArray(pSource)) {
        // c'est un array, on effectue un clône sur la collection
        this._source = [...pSource];
      } else {
        // observable, attente du résultat
        this.connectToObs(pSource);
      }
    } else {
      // pas de paramètre, collection vide
      this._source = [];
    }
  }

  /**
   * Se connecte à l'observable donné en paramètre
   * Dés que l'observable fournit une nouvelle valeur, la datasource est reconstruit et réapplique les tris et filtres éventuels
   * @param pObservable L'observable auquel la datasource doit se connecter
   */
  private connectToObs(pObservable: Observable<T[]>): void {
    pObservable
      .pipe(
        first(), // on appelle l'observable une seule fois et on kill
        tap((value: T[]) => (this._source = [...value]))
      )
      .subscribe();
  }

  // ####
  // ####
  // #### accesseurs externes
  // ####
  // ####

  // accesseur sur la source (lecture seule)
  public getSource(): T[] {
    return this._source;
  }

  public setSource(pValue: T[]): this {
    this._source = pValue;

    if (this._isHierarchical) {
      this._needRebuild = true;
    }

    this._refreshSubject.next();
    return this;
  }

  // accesseur sur la source de travail (lecture seule, compo uniquement)
  public getWorkedSource(): any[] {
    return this._workedSource;
  }

  public get filterFuncs(): WDSFilterFunction<T>[] {
    return this._filterFuncs;
  }

  // ####
  // ####
  // #### configuration
  // ####
  // ####

  /** accesseur sur mode de sélection (pour affichage checkBox dans Tree) */
  public get selectionMode(): WDSSelectionMode {
    return this._selectionMode;
  }
  /** mode de sélection mono / multi / sans */
  public setSelectionMode(pMode: WDSSelectionMode): this {
    if (this._selectionMode === pMode) {
      // mode identique, on évite les traitements inutiles
      return this;
    }

    if (!!this._selectionMode) {
      // sur changement de mode de sélection on reset la selection actuelle
      this.setSelectedItems([]); // _selectedItems = [];
    }
    this._selectionMode = pMode;

    // La dataSource était construite, on dispatch le changement dans la sélection
    if (this._finalized) {
      this._changes.next({
        type: 'programmaticChanged',
        value: this._selectedItems,
      });
    }
    return this;
  }

  /**
   * Affectation de la filterFunc(externe) à utiliser sur la datasource
   *
   * Warning: cette fonction override toutes les fonctions de filtre actives.
   * Si vous ne souhaitez pas les overrider, voir la méthode addFilter pour ajouter une fonction de filtre
   *
   * @param pFilterFunction La filter function à utiliser
   */
  public setFilter(pFilterFunction: WDSFilterFunction<T>): this {
    this._filterFuncs = [pFilterFunction];
    return this;
  }

  /**
   * Ajout d'une filter function à la liste des filter function actives.
   * Il est possible d'avoir plusieurs filter functions actives sur une datasource
   * Celà afin d'avoir par exemple une fonction de filtre dans la vue et une fonction de filtre en header de grid par exemple
   * @param pFilterFunction la fonction de filtre à ajouter à la liste
   */
  public addFilter(pFilterFunction: WDSFilterFunction<T>): this {
    if (!this._filterFuncs) {
      this._filterFuncs = [pFilterFunction];
    } else {
      this._filterFuncs.push(pFilterFunction);
    }

    return this;
  }

  /**
   * Supprime une filter function de la liste des filters functions
   */
  public removeFilter(pFilterFunction: WDSFilterFunction<T>): void {
    if (!this._filterFuncs) {
      return;
    }

    const idx: number = this._filterFuncs.indexOf(pFilterFunction);

    if (idx > -1) {
      this._filterFuncs.splice(idx, 1);
    }
  }

  /**
   * Affectation des éléments sélectionnés
   * @param pSelectedItems une collection d'éléments de la datasource pour sélection initiale
   */
  public setSelectedItems(pSelectedItems: T[], pEmitChanges: boolean = false): this {
    this._selectedItems = pSelectedItems;
    if (pEmitChanges) {
      this._changes.next({
        type: 'selectionChanged',
        value: this._selectedItems,
      });
    }
    return this;
  }

  /**
   * Sélection de tous les éléments affichés
   */
  public selectAll(): this {
    if (this._workedSource) {
      this._selectedItems = [...this._workedSource];
    } else if (this._source) {
      this._selectedItems = [...this._source];
    }
    return this;
  }

  /**
   * Affectation des champs à utiliser pour trier la collection
   * @param pFields Les champs de tris à utiliser
   */
  public setSortFields(pFields: WDSSortField[]): this {
    this._sortFields = pFields;
    this._sortFn = null;
    this._sortChanged = true;
    return this;
  }

  /**
   * Accesseur aux filtres actifs
   */
  public get sortFields(): WDSSortField[] {
    return this._sortFields;
  }

  /** Affectation d'un champ pour filtrer la collection */
  public setSortField(pField: WDSSortField): this {
    return this.setSortFields([pField]);
  }

  /** Affectation d'un champ numérique pour filtrer la collection */
  public setNumericSortField(property: string, way: WDSSortFieldWay = 'asc'): this {
    return this.setSortField({
      property,
      way,
      numeric: true,
    });
  }

  /** Fonction de tri custom */
  public setSortFn(pSortFn: (itemA: T, itemB: T) => number): this {
    this._sortFn = pSortFn;
    this._sortFields = null;
    this._sortChanged = true;
    return this;
  }

  /**
   * Accesseur à l'observable de suivi des changements sur les champs de tri
   */
  public get sortChange(): Observable<WDSSortField[]> {
    return this._sortChange.asObservable();
  }

  private _needRebuild: boolean = true;

  /**
   * Construction de la dataSource
   */
  public build(): this {
    if (this._finalized) {
      // on ne construit pas deux fois la datasource
      return this;
    }

    this._refreshSubject = new BehaviorSubject<void>(null);
    this._changes = new BehaviorSubject<WDSChanges>(null);
    this._sortChange = new BehaviorSubject<WDSSortField[]>(null);

    // on écoute les demandes de refresh pour reconstruire l'application
    this._refreshSubject
      .pipe(
        // application du sort (donne sortedArray)
        map((_) => this.doSort(this._source)),
        // application des filtres (donne FilterdArray)
        map((sortedArray) => this.doFilter(sortedArray)),
        // construction de la worked source
        tap((filteredArray) => {
          // on met à jour la source travaillée
          this._workedSource = [...filteredArray]; // attention aux crochets, sinon pas de dispatch
          // on émit l'événement de refresh des datas
          // on fait la distinction entre le premier build et les refresh pour apporter de la précision
          this._changes.next({
            type: this._finalized ? 'refreshed' : 'init',
            value: this._workedSource,
          });

          this.updateRenderedData();
        })
      )
      .subscribe();

    // si la datasource gère également le hierarchique. On ajoute aux étapes de build
    if (this._isHierarchical) {
      // alimentation initiale de la map des noeuds ouverts
      if (this._openNodes) {
        this._openedNodeMap = new Map(this._openNodes.map((obj) => [obj[this.itemCodeField], obj]));
      }

      this._refreshSubject
        .pipe(
          tap((_) => {
            if (this._needRebuild) {
              this._needRebuild = false;
              this.buildRawSource();
              this.buildTree();
            }
          }),
          tap((_) => {
            // alimentation de la collection de travail
            // gestion des noeuds ouverts
            this._treeWorkedSource = [...this.regenerateFlatSource()];

            // on émit l'événement de refresh des datas
            // on fait la distinction entre le premier build et les refresh pour apporter de la précision
            this._changes.next({
              type: this._finalized ? 'treeRefreshed' : 'treeInit',
              value: this._workedSource,
            });
          })
        )
        .subscribe();
    }

    this._finalized = true;
    return this;
  }

  /**
   * Les opérations à effectuer sur le refresh
   */
  protected handleRefresh(obs: Observable<void>): Observable<any> {
    return;
  }

  /**
   * Retourne l'observable de changement de la datasource pour s'y abonner
   */
  public get changes(): Observable<WDSChanges> {
    return this._changes.asObservable();
  }

  /**
   * Le dernier changement opéré sur la datasource
   */
  public get lastChange(): WDSChanges {
    return this._changes.getValue();
  }

  /**
   * Rafraichit la dataSource
   * Les filtres et les tris sont réappliqués.
   * Les selectedItems sont vérifiées
   * Les Observables sont prévenus des changements pour gestion du rafraîchissement
   */
  public refresh(): void {
    if (!this._finalized) {
      throw new Error(
        'Cannot call the refresh method on a Datasource that has not been built. Please make sure you called the build method.'
      );
    }
    this._refreshSubject.next();
  }

  // ####
  // ####
  // #### traitements internes
  // ####
  // ####
  /**
   * Implémentation de l'iterable pour utiliser les boucles for directement sur la datasource
   */
  [Symbol.iterator](): Iterator<T> {
    let index: number = 0;
    const iteratorArray: T[] = [...this._workedSource];
    return {
      next(): IteratorResult<T> {
        return {
          value: iteratorArray[index++],
          done: index === iteratorArray.length + 1,
        };
      },
    };
  }

  /**
   * Implémentation du tri générique (sur la source, sans la modifier).
   * @private
   */
  public doSort(pSource: T[]): T[] {
    if (this._ignoreSort) {
      return pSource;
    }
    if (this._sortChanged) {
      this._sortChanged = false;
      this._sortChange.next(this._sortFields);
    }
    if (this._sortFields?.length > 0) {
      return WizCollectionUtils.doSort(pSource, this._sortFields);
    } else if (this._sortFn) {
      return pSource?.sort(this._sortFn);
    }
    return pSource;
  }

  /**
   * Implémentation de l'application du filtre sur la datasource
   * @private
   */
  public doFilter(pSource: T[]): T[] {
    if (pSource && pSource.length && this._filterFuncs && this._filterFuncs.length) {
      // si il n'y a qu'une seule fonction de filtre actives on la passe directement à la fonction filter de l'array
      // cela évite d'effectuer un every inutile sur le tableau des filter functions
      if (this._filterFuncs.length === 1) {
        return pSource.filter(this._filterFuncs[0]);
      } else {
        // il y a plusieurs filtres actifs, on fait un every sur chaque fonction de filtre pour filtrer un item
        return pSource.filter((item: T, idx: number, ar: T[]) => this._filterFuncs.every((f) => f(item, idx, ar)));
      }
    }

    return pSource;
  }

  /**
   * Vérifie si un item est filtré ou non
   */
  public checkItemFilter(item: T): boolean {
    if (!this._filterFuncs || !this._filterFuncs.length) {
      return true;
    }

    return this._filterFuncs.every((f) => f(item));
  }

  /**
   * Annule la sélection en cours
   */
  public clearSelection(): void {
    this._selectedItems = [];
    this._changes.next({
      type: 'selectionChanged',
      value: this._selectedItems,
    });
  }

  /**
   * Sélectionne / déselectionne l'item passé en paramètre : PAR un HUMAIN
   * NE PAS UTILISER en mode "programmatic"
   * @param pItem L'item à selectionner / déselectionner
   */
  public toggle(pItem: T | number, emitChanges: boolean = true): void {
    // mode none ou l'item n'est pas dans la collection travaillée => pas de sélection
    const requestedItem: T = !isNaN(pItem as number) ? this.getItem(pItem as number) : (pItem as T);
    let isItemAdded: boolean;

    if (!requestedItem || this._selectionMode === 'none' || !this._workedSource.includes(requestedItem)) {
      return;
    }

    if (this._isHierarchical) {
      this.toggleHierarchical(pItem, false, emitChanges);
    } else {
      // gestion de la sélection
      if (this._selectionMode === 'single') {
        // sélection ou déselection
        isItemAdded = this.isSelected(requestedItem);
        this._selectedItems = isItemAdded ? [] : [requestedItem];
      } else {
        // blindage si selectedItems = null
        if (!this._selectedItems) {
          this._selectedItems = [];
        }
        const selectedIdx: number = this._selectedItems.indexOf(requestedItem);
        isItemAdded = selectedIdx === -1;
        if (isItemAdded) {
          this._selectedItems.push(requestedItem); // sélection
        } else {
          this._selectedItems.splice(selectedIdx, 1); // déselection
        }
      }
      if (emitChanges) {
        this._changes.next({
          type: 'selectionChanged',
          value: this._selectedItems,
          item: requestedItem,
          action: isItemAdded ? 'added' : 'deleted',
        });
      }
    }
  }

  /**
   * Modifie la sélection pour un ou plusieurs items
   * @param pValue
   */
  public toggleMultiple(pValue: T[]): void {
    if (!pValue || pValue.length === 0) {
      return;
    }

    pValue.forEach((v) => {
      this.toggle(v, false);
    });

    this._changes.next({
      type: 'selectionChanged',
      value: this._selectedItems,
    });
  }

  /** Depuis un affichage en mode tree pour gérer la sélection des sous-éléments */
  public toggleHierarchical(pItem: T | number, pCheckSource: boolean = true, pEmitChanges: boolean = true): void {
    // mode none ou l'item n'est pas dans la collection travaillée => pas de sélection
    let requestedItem: T = null;
    if (!isNaN(pItem as number)) {
      requestedItem = this.getItem(pItem as number);
    } else {
      requestedItem = pItem as T;
    }

    const wrapper: TreeItemWrapper<T> = this.getItemWrapper(requestedItem);

    if (pCheckSource) {
      if (!requestedItem || this._selectionMode === 'none' || !this._treeWorkedSource.includes(wrapper)) {
        return;
      }
    }

    if (this.selectionMode === 'single') {
      // mode single, switch, et remove self si déjà selected
      // sélection ou déselection
      this._selectedItems = this.isSelected(requestedItem) ? [] : [requestedItem];
    } else {
      // mode multi, add / delete en cascade
      // la sélection contient les objets métiers
      if (this.isSelected(requestedItem)) {
        this.innerDeleteSelectedItem(wrapper);
        this.innerDeleteSelectedToRoot(wrapper);
      } else {
        this.innerAddSelectedItem(wrapper);
        this.innerAddSelectedToRoot(wrapper);
      }
    }

    if (pEmitChanges) {
      this._changes.next({
        type: 'selectionChanged',
        value: this._selectedItems,
      });
    }
  }

  /**
   * Sélectionne tous les éléments passés en paramètre.
   *
   * Agit comme la fonction select() sauf qu'elle va dispatcher un event de 'selectionChanged' indiquant une interaction avec l'humain
   */
  public commitSelection(pItems: T[]): void {
    this._selectedItems = !!pItems ? [...pItems] : [];
    this._changes.next({
      type: 'selectionChanged',
      value: this._selectedItems,
    });
  }

  /**
   * Selectionne un ou plusieurs éléments (appel externe)
   * @param pItems Les items à sélectionner
   */
  public select(pItems: T[]): void {
    this._selectedItems = !!pItems ? [...pItems] : [];
    this._changes.next({
      type: 'programmaticChanged',
      value: this._selectedItems,
    });
  }

  /**
   * Ajoute un ou des items à la datasource
   * Un refresh est effectué après ajout
   *
   * @param pValue La ou les valeurs à ajouter
   * @param index optional L'index à partir duquel ajouter les éléments
   */
  public addItem(pValue: T | T[], index: number | null = null): void {
    if (index !== null && !isNaN(index) && index < this.length) {
      Array.isArray(pValue) ? this._source.splice(index, 0, ...pValue) : this._source.splice(index, 0, pValue);
    } else {
      Array.isArray(pValue) ? this._source.push(...pValue) : this._source.push(pValue as T);
    }
    this._refreshSubject.next();
  }

  /**
   * Supprime un item de la datasource
   * Un refresh est effectué après ajout
   *
   * @param pValue L'item ou l'index de l'item à supprimer
   */
  public removeItem(pValue: T | number): void {
    if (!isNaN(pValue as number)) {
      this._source.splice(pValue as number, 1);
    } else {
      const idx: number = this._source.indexOf(pValue as T);
      if (idx >= 0) {
        this._source.splice(idx, 1);
      }
    }
    this._refreshSubject.next(); // on refresh
  }

  /**
   * Déplace l'item donné à l'index demandé
   *
   * @param pValue L'item à déplacer
   * @param index L'index vers lequel déplacer l'item
   */
  public moveItem(pFromIndex: number, pToIndex: number): this {
    // Un tri / sort peut avoir été appliqué, les objets de la collection de travail sont
    // récupérés pour déterminer leur "vrai" index :
    const fromItem: T = this._workedSource[pFromIndex < 0 ? 0 : pFromIndex];
    let dropped: boolean = false;
    // console.log('fromItem', fromItem);

    let newOrder: any[] = [];
    let idx: number = 0;
    this._workedSource.forEach(item => {
      if(idx === pToIndex) {
        newOrder.push(fromItem);
        dropped = true;
        // console.log('fromItem', fromItem, idx);
      }
      if(item !== fromItem) {
        newOrder.push(item);
        // console.log('item', item, idx);
      }
      idx++;
    });

    if(!dropped) { // dépose en fin
      newOrder.push(fromItem);
    }

    this._source = newOrder;
    this._workedSource = newOrder;
    const toItem: T =
      this._workedSource[pToIndex > this._workedSource.length - 1 ? this._workedSource.length - 1 : pToIndex];

    const trueFromIndex: number = this._source.indexOf(fromItem);
    const trueToIndex: number = this._source.indexOf(toItem);


    this._source.splice(trueToIndex, 0, this._source.splice(trueFromIndex, 1)[0]);


    // On skippe le re-sort au prochain refresh, ou le moveItem va être annulé.
    this._ignoreSort = true;

    // Dispatch d'un event spécifique de réordonnencement
    this._refreshSubject.next();
    this._changes.next({ type: 'orderChanged', value: this._workedSource });

    return this;
  }

  /**
   * Indique à la datasource qu'un ou plusieurs items on été mis à jour
   *
   * La datasource ne fait rien de particulier sur les items. Elle previent simplement de la mise à jour des items.
   */
  public itemUpdated(pItems: T[]): void {
    this._changes.next({ type: 'itemUpdated', value: pItems });
  }

  // ####
  // ####
  // #### accesseurs et métriques
  // ####
  // ####

  /**
   * La taille de la datasource dans son état actuel (filtrée et travaillée)
   */
  public get length(): number {
    return this._workedSource.length;
  }
  /**
   * Returns the first item of the datasource
   */
  public get first(): T {
    return this._workedSource && this._workedSource.length ? this._workedSource[0] : null;
  }

  public get last(): T {
    return this._workedSource && this._workedSource.length ? this._workedSource[this._workedSource.length - 1] : null;
  }

  /**
   * Retourne l'item à l'index donné (ou le TIW)
   * @param pIndex l'index de l'élément recherché
   * @param pFromHierarchical Indique que l'on recherche l'index dans la datasource travaillée
   */
  public getItem(pIndex: number, pFromHierarchical: boolean = false): T {
    if (!pFromHierarchical) {
      // vérifie qu'on ne cherche pas un mauvais index
      if (pIndex >= this.length || pIndex < 0) {
        return null;
      }

      return this._workedSource[pIndex];
    } else {
      if (pIndex >= this._treeWorkedSource.length || pIndex < 0) {
        return null;
      }

      return this._treeWorkedSource[pIndex].source;
    }
  }

  /**
   * Recherche l'index de l'item dans la datasource travaillée
   * @param pItem L'item recherché
   */
  public getItemIndex(pItem: T): number {
    return this._workedSource && this._workedSource.length ? this._workedSource.indexOf(pItem) : -1;
  }

  /**
   * Vérifie si l'item est contenu dans la datasource dans son état actuel (filtré)
   * @param pItem L'item recherché
   */
  public contains(pItem: T): boolean {
    if (!this._workedSource || !this._workedSource.length) {
      return false;
    }
    return this._workedSource.includes(pItem);
  }

  /** Recherche dans la dataSource d'un élément validant la fonction prédicat. */
  public find(pPredicate: (value: T, index?: number) => boolean): T | undefined {
    return this._workedSource?.find(pPredicate) || undefined;
  }

  /**
   * Renvoie la selection actuelle (one / many / null)
   */
  public get selection(): T | T[] | null {
    // Pas de sélection, on renvoie null
    if (!this._selectedItems || this._selectedItems.length <= 0 || this._selectionMode === 'none') {
      return null;
    }

    // Renvoi un array si en mode multiple ou l'item en mode simple
    return this._selectionMode === 'multiple' ? this._selectedItems : this._selectedItems[0];
  }

  /**
   * Retourne la sélection forcée en mode array
   */
  public get selectedItems(): T[] {
    if (this._selectionMode === 'none') {
      return null;
    }
    return this._selectedItems;
  }

  /**
   * Retourne l'item selectionnée en cas de mode de sélection single
   */
  public get selectedItem(): T {
    if (this._selectionMode === 'multiple') {
      return null;
    }

    // check si les selectedItems sont alimentés
    return !!this._selectedItems && this._selectedItems.length > 0 ? this._selectedItems[0] : null;
  }

  /**
   * Vérifie si un item est sélectionné
   *
   * @param pItem L'item à vérifier
   */
  public isSelected(pItem: T): boolean {
    return !!this._selectedItems && this._selectedItems.length > 0 ? this._selectedItems.includes(pItem) : false;
  }

  // ####
  // ####
  // #### Implémentation des méthodes de la classes DataSource d'angular
  // #### Le virtual scroll communique avec la dataSource via le CollectionViewer
  // ####

  /**
   * Accès à l'observable des renderedData
   */
  public get renderedDataObs(): Observable<T[]> {
    return this._renderedData.asObservable();
  }

  /**
   * Les données actuellement rendues
   */
  public get renderedData(): T[] {
    return this._renderedData ? this._renderedData.value : null;
  }

  /**
   * La longueur des données actuellement rendues
   */
  public get renderLength(): number {
    return this._renderedData.value.length;
  }

  /** Returns the starting index of the rendered data */
  public get renderStart(): number {
    return this._lastRange ? this._lastRange.start : 0;
  }

  connect(collectionViewer?: CollectionViewer): Observable<T[] | readonly T[]> {
    this.handleCollectionViewer(collectionViewer);

    return this._renderedData;
  }
  disconnect(collectionViewer?: CollectionViewer): void {
    this._renderedData.complete();
  }

  /**
   * La dernière range d'affichage demandée
   */
  private _lastRange: ListRange;

  /**
   * Gestion du collection viewer
   *
   * Le collection viewer est donnée par le virtualScroll lorsqu'il souhaite s'abonner aux changements sur la datasource
   * La datasource doit également s'abonner au collection viewer pour réagir aux changements de la vue.
   * C'est de cette façon que le virtual scroll donne l'indication à la datasource des donées à afficher.
   */
  protected handleCollectionViewer(cv: CollectionViewer): void {
    cv.viewChange.subscribe((range) => {
      if (this._lastRange && this._lastRange.start === range.start && this._lastRange.end === range.end) {
        return;
      }

      this._lastRange = range;
      this.updateRenderedData();
    });
  }

  /**
   * Retourne la portion de données demandées par l'objet ListRange
   */
  private trimToRange(range: ListRange, data: T[]): T[] {
    return data.slice(range.start, range.end);
  }

  /**
   * Construit l'array d'objets à rendre et effectue un next sur l'observable
   */
  protected updateRenderedData(): void {
    const data: T[] = this._lastRange ? this.trimToRange(this._lastRange, this._workedSource) : [];
    this._renderedData.next(data);
  }

  /**
   * clone de la collection cible (nouvelle instance)
   * @param keepFilter garder la filterFunc, default = false
   * @param keepSelection garder la sélection, default = false
   * @param keepSort garder le tri, default = true
   * @param keepSelectionMode garder le mode de sélection (mono/multi), default = true
   */
  public clone(
    keepFilter: boolean = false,
    keepSelection: boolean = false,
    keepSort: boolean = true,
    keepSelectionMode: boolean = true
  ): WizDatasource<T> {
    const clone: WizDatasource<T> = new WizDatasource<T>(this._source)
      .setHierarchical(this._isHierarchical)
      .setItemCodeField(this._itemCodeField)
      .setItemParentCodeField(this._itemParentCodeField);
    if (keepFilter) {
      clone._filterFuncs = this._filterFuncs ? [...this._filterFuncs] : [];
    }
    if (keepSelection) {
      clone.setSelectedItems(this._selectedItems ? [...this._selectedItems] : []);
    }
    if (keepSort) {
      clone.setSortFields(this._sortFields ? [...this._sortFields] : []);
    }
    if (keepSelectionMode) {
      clone.setSelectionMode(this._selectionMode);
    }

    return clone.build();
  }

  /* ########################################
            GESTION DU MODE HIERARCHIQUE
  ########################################## */
  private _isHierarchical: boolean = false;

  private _itemCodeField: string = 'code'; // le champ de référence pour identifier un objet unique
  private _itemParentCodeField: string = 'parentCode'; // le champ portant la référence unique du parent
  private _openNodes: T[] = null; // les instances T de noeuds ouverts (origine)

  /**
   * la map des objets sources encapsulés en TIW. l'objet source est la clé
   * map[code/champ ref de l'objet] = TIW
   * Construction sur build
   */
  private _rawSourceMap: Map<string, TreeItemWrapper<T>> = new Map<string, TreeItemWrapper<T>>();
  /** l'arbre (complet), construit sur la base de _rawSourceMap (contient les éléments de 1er niveau / root) */
  private _treeSource: TreeItemWrapper<T>[] = null;
  /** La source du tri travaillée */
  private _treeWorkedSource: TreeItemWrapper<T>[];
  /** map de noeuds ouverts map[code] = objet source */
  public _openedNodeMap: Map<string, T> = new Map<string, T>();

  /**
   * Indique, au moment de la construction si la datasource doit également gérer le mode hierarchique
   *
   * Par défaut, la datasource n'est pas hierarchique
   * @param pValue true si hiérarchique, false sinon
   * @returns this
   */
  public setHierarchical(pValue: boolean): this {
    this._isHierarchical = pValue;
    return this;
  }

  // le champ qui contient la valeur de référence (comme parent)
  public set itemCodeField(v: string) {
    this._itemCodeField = v;
  }
  public get itemCodeField(): string {
    return this._itemCodeField;
  }
  /**
   * Affectation du champ unique de référence pour les objets
   * @param pItemCodeField le nom du champ (défaut = code)
   */
  public setItemCodeField(pItemCodeField: string): this {
    this._itemCodeField = pItemCodeField;
    return this;
  }

  /**
   * Affectation du champ unique de référence au parent
   * @param pItemCodeField le nom du champ (défaut = parentCode)
   */
  public setItemParentCodeField(pItemParentCodeField: string): this {
    this._itemParentCodeField = pItemParentCodeField;
    return this;
  }

  // le champ qui contient la référence au champ parent
  public set itemParentCodeField(v: string) {
    this._itemParentCodeField = v;
  }
  public get itemParentCodeField(): string {
    return this._itemParentCodeField;
  }
  // les noeuds ouvert (alim externe)
  public setOpenNodes(value: T[]): this {
    this._openNodes = value;

    // La dataSource était construite, on dispatch le changement pour refresh de la flatSource
    if (this._finalized) {
      this._openedNodeMap = new Map(this._openNodes.map((obj) => [obj[this.itemCodeField], obj]));
      this._refreshSubject.next();
    }
    return this;
  }
  public get openNodes(): T[] {
    return this._openedNodeMap ? Array.from(this._openedNodeMap.values()) : [];
  }

  /**
   * accesseur par itemCodeField (pour identification externe du VO sur filterContext)
   * @param pKey la clé de l'item recherché
   */
  public getItemByKey(key: string): T {
    return new Map(this._source.map((i) => [i[this._itemCodeField], i])).get(key);
  }

  /**
   * accesseur sur le wrapper d'un objet métier pour collecte de sa structure gigogne
   * @param pItem L'item recherché
   */
  public getItemWrapper(pItem: T): TreeItemWrapper<T> {
    return this._rawSourceMap.get(pItem[this._itemCodeField]);
  }

  // ####
  // ####
  // #### ouverture / fermeture des noeuds ###
  // ####
  // ####

  /** flag pour gestion de l'icone des noeuds */
  public isNodeExpanded(tiw: TreeItemWrapper<T>): boolean {
    return this._openedNodeMap.get(tiw.code) != null;
  }
  /**
   * dispatch interne de l'event de manipulation de noeud
   * value porte les noeuds modifiés / tout / rien
   */
  private innerNodeUpdate(nodes: T[]): void {
    // demande de modif, pour reconstruction de l'arbre
    if (this._refreshSubject) {
      this._refreshSubject.next();
    }

    // La dataSource était construite, on dispatch le changement pour refresh de la flatSource
    if (this._finalized) {
      this._changes.next({ type: 'nodeChanged', value: nodes });
    }
  }

  /**
   * Toggle node
   * Expand or collpase node depending of status
   */
  public toggleNode(tiw: TreeItemWrapper<T>) {
    if (this._openedNodeMap.get(tiw.code)) {
      // déjà présent, collapse
      this._openedNodeMap.delete(tiw.code);
    } else {
      // expand
      this._openedNodeMap.set(tiw.code, tiw.source);
    }
    this.innerNodeUpdate([tiw.source]); // signaler le changement
  }

  /** Expand all node of tree */
  public expandAll(): void {
    this._rawSourceMap.forEach((tiw: TreeItemWrapper<T>) => {
      this._openedNodeMap.set(tiw.code, tiw.source);
    });
    this.innerNodeUpdate(this.openNodes); // signaler le changement
  }
  /** Collapse all node of tree */
  public collapseAll(): void {
    this._openedNodeMap = new Map<string, any>();
    this.innerNodeUpdate(null); // signaler le changement
  }

  /** Expand node of tree depuis l'objet métier */
  public expandNode(item: T): void {
    this._openedNodeMap.set(item[this.itemCodeField], item);
    this.innerNodeUpdate([item]); // signaler le changement
  }

  /** Collapse node of tree */
  public collapseNode(item: T): void {
    this._openedNodeMap.delete(item[this.itemCodeField]);
    this.innerNodeUpdate([item]); // signaler le changement
  }
  /** Expand un noeud récursivement, on va s'assurer que les parents soient également expanded */
  public expandNodeRecursively(item: TreeItemWrapper<T>): void {
    const nodesToExpand: TreeItemWrapper<T>[] = [item];
    let parent: TreeItemWrapper<T> = item.getParent();

    // on identifie tous les noeud à expand
    while (!!parent && !this.isNodeExpanded(parent)) {
      nodesToExpand.unshift(parent);
      parent = parent.getParent();
    }

    // alimentation de la map des noeuds expanded
    nodesToExpand.forEach((tiw) => this._openedNodeMap.set(tiw.code, tiw.source));
    // signalement de la mise à jour
    this.innerNodeUpdate(nodesToExpand.map((tiw) => tiw.source));
  }

  // ####
  // ####
  // #### construction du tree ###
  // ####
  // ####

  /** construction de la map <item> => TIW(item), unique sur build */
  private buildRawSource(): void {
    // création de la Map par instance d'objet source (alimente _rawSourceMap)
    this._rawSourceMap = new Map(
      this._source.map((obj) => [
        obj[this.itemCodeField],
        new TreeItemWrapper(obj, this.itemCodeField, this.itemParentCodeField),
      ])
    );
  }

  /** construction de l'arbre des Tiw, unique sur build */
  private buildTree(): void {
    // le futur résultat
    let result: TreeItemWrapper<T>[] = [];

    // alimentation des children dans la _rawSource
    // pour les items de plus haut niveau (pas de parent), alimentation de la tmpSource (future Flat)
    this._source.forEach((sourceItem: T) => {
      // prise du TIW dans la rawSource
      const item: TreeItemWrapper<T> = this._rawSourceMap.get(sourceItem[this.itemCodeField]);
      if (!WizStringUtils.isEmpty(item.parentCode) && this._rawSourceMap.has(item.parentCode)) {
        // ajout au parent s'il existe
        this._rawSourceMap.get(item.parentCode).addChild(item);
      } else {
        // root
        result.push(item);
      }
    });

    if (this._sortFields?.length) {
      result = this.doRecurseSort(
        result,
        this._sortFields.map((sf) => ({
          ...sf,
          property: `source.${sf.property}`,
        }))
      );
    }
    this._treeSource = result; // alimentation du jeu de données TIW
  }

  /**
   * construction de la liste de rendu (à plat)
   * selon l'arbre : treeSource
   * selon la collection de noeud ouverts : _openedNodeMap
   * en tenant compte de la filterFunc
   * Sur chaque refresh
   */
  private regenerateFlatSource(): TreeItemWrapper<T>[] {
    const result: TreeItemWrapper<T>[] = []; // la collection de destination
    // filtre : retourne une collection de code à maintenir (par défaut = _rawSourceMap )
    const aliveNodes: Map<string, TreeItemWrapper<T>> = this.applyFilterFunc();

    // gestion des noeuds ouverts
    this._treeSource.forEach((item: TreeItemWrapper<T>) => {
      this.buildFlatSourceRecurse(item, result, aliveNodes);
    });

    return result;
  }

  public getTreeWorkedSource(): TreeItemWrapper<T>[] {
    return this._treeWorkedSource;
  }

  /**
   * Retourne la data source ordonnée comme l'est l'arbre de données associé.
   *
   * @returns data source ordonnée comme l'arbre associé
   */
  public get treeFlatDataSource(): T[] {
    return this._treeWorkedSource?.map(({ code }) => this._openedNodeMap.get(code)) || [];
  }

  /** traitement récursif de construction de la liste à plat
   * @param item : le tiw en cours d'analyse
   * @param result : l'arbre en cours de contruction
   * @param aliveNodes: une map de code de tiw à garder (pas filtrés)
   */
  private buildFlatSourceRecurse(
    item: TreeItemWrapper<T>,
    result: TreeItemWrapper<T>[],
    aliveNodes: Map<string, TreeItemWrapper<T>>
  ): void {
    // j'ajoute l'item s'il n'est pas filtré
    if (aliveNodes.get(item.code)) {
      result.push(item);

      // s'il est ouvert, j'ajoute ses enfants
      if (item.hasChildren && this._openedNodeMap.get(item.code)) {
        item.children.forEach((subItem: TreeItemWrapper<T>) => {
          this.buildFlatSourceRecurse(subItem, result, aliveNodes);
        });
      }
    }
  }

  /** application de la filterFunc sur le tree complet en préparation */
  private applyFilterFunc(): Map<string, TreeItemWrapper<T>> {
    let result: Map<string, TreeItemWrapper<T>> = new Map<string, TreeItemWrapper<T>>();

    if (!this._filterFuncs || !this._filterFuncs.length) {
      // pas de filterFunc, pas de traitement
      result = new Map(this._rawSourceMap);
      return result;
    }
    // traitement from Bottom
    const rawSource = [...this._rawSourceMap.values()].filter((item: TreeItemWrapper<T>) => !item.hasChildren);
    if (rawSource?.length === this._rawSourceMap?.size) { // tout le monde à plat, je n'ai qu'un niveau
      rawSource.forEach((item: TreeItemWrapper<T>) => {
        // contrôle de présence pour self (recyclage de la filter du _super)
        if (this.checkItemFilter(item.source)) {
          result.set(item.code, item);
        }
      });
    } else {
      this.applyFilterFuncRecurse(rawSource, result);
    }


    return result;
  }


  /**
   * filterFunc récursive. Va appeler les parents sur fin de pile
   * @param items : les tiw en cours d'analyse
   * @param result : la map<string, Tiw> des éléments à garder
   */
  private applyFilterFuncRecurse(items: TreeItemWrapper<T>[], result: Map<string, TreeItemWrapper<T>>): void {
    // collecte des parents pour traitement suivant
    const parentsToCheck: Map<string, TreeItemWrapper<T>> = new Map<string, TreeItemWrapper<T>>();
    items.forEach((item: TreeItemWrapper<T>) => {
      // contrôle de présence pour self (recyclage de la filter du _super)
      if (this.checkItemFilter(item.source)) {
        result.set(item.code, item);
      }
      // parent ?
      const daddy: TreeItemWrapper<T> = this._rawSourceMap.get(item.parentCode);
      if (daddy) {
        // ajout du parent à la pile pour prochain traitement
        parentsToCheck.set(daddy.code, daddy);

        // si je suis dans le result, j'ajoute d'office mon père
        // attention le get est nécessaire, j'ai peut-être été ajouté par un enfant
        if (result.get(item.code)) {
          result.set(daddy.code, daddy);
        }
      }
    });

    if (parentsToCheck.size > 0) {
      const parentRaw = new Array(...parentsToCheck).map((pairs) => pairs[1]);
      this.applyFilterFuncRecurse(parentRaw, result);
    }
  }

  /**
   * Effectue le sort mais en récursif sur les TIW
   */
  protected doRecurseSort(pSource: TreeItemWrapper<T>[], pFields: WDSSortField[]): TreeItemWrapper<T>[] {
    const result = WizCollectionUtils.doSort(pSource, pFields);
    result.forEach((tiw: TreeItemWrapper<T>) => {
      if (tiw.hasChildren) {
        tiw.children = this.doRecurseSort(tiw.children, pFields);
      }
    });
    return result;
  }

  // ####
  // ####
  // #### gestion de la sélection (pas de refresh du build nécessaire) ###
  // ####
  // ####

  // partiellement sélectionné : des enfants select, mais pas tous
  // le test "selected" est toujours fait avant
  public isPartialSelected(item: TreeItemWrapper<T>): boolean {
    if (!item.hasChildren) {
      // pas d'enfants, pas possible
      return false;
    }
    // combien d'enfants ?
    const childrenCount: number = item.children.length;
    // combien d'enfants sélectionnés
    const childrenSelectedCount: number = item.children.filter((subItem: TreeItemWrapper<T>) =>
      this.isSelected(subItem.source)
    ).length;

    const childrenPartialSelectedCount: number = item.children.filter((subItem: TreeItemWrapper<T>) =>
      this.isPartialSelected(subItem)
    ).length;

    if ((childrenSelectedCount > 0 && childrenCount !== childrenSelectedCount) || childrenPartialSelectedCount > 0) {
      // pas tous sélectionné, mais au moins 1
      return true;
    }
    return false;
  }

  /**
   * ajout d'un élément à la sélection depuis l'extérieur
   * @param item : une instance métier de la datasource
   * Va appeler innerAddSelectedItem
   */
  public addSelectedItem(item: T): void {
    // collecte du Tiw lié
    const tiw: TreeItemWrapper<T> = this._rawSourceMap.get(item[this.itemCodeField]);
    this.innerAddSelectedItem(tiw);
    // traitement ascendant : si je viens de sélectionner le dernier enfant, select du parent
    this.innerAddSelectedToRoot(tiw);
  }

  /** ajout interne de selected, utilise TIW */
  private innerAddSelectedItem(tiw: TreeItemWrapper<T>): void {
    this._selectedItems.push(tiw.source);
    if (tiw.children) {
      // si enfants, application du selected
      tiw.children.forEach((susbTiw: TreeItemWrapper<T>) => {
        this.innerAddSelectedItem(susbTiw);
      });
    }
  }

  /** traitement ascendant : si je viens de sélectionner le dernier enfant, select du parent */
  private innerAddSelectedToRoot(tiw: TreeItemWrapper<T>): void {
    if (!WizStringUtils.isEmpty(tiw.parentCode)) {
      // si j'ai un parent
      const parentTiw: TreeItemWrapper<T> = this._rawSourceMap.get(tiw.parentCode);
      if (!this.isPartialSelected(parentTiw)) {
        // pas partiel. Comme je viens d'en cocher un, je suis forcément complet
        this._selectedItems.push(parentTiw.source);
        this.innerAddSelectedToRoot(parentTiw); // contrôle récursif
      }
    }
  }

  /**
   * suppression d'un élément à la sélection depuis l'extérieur
   * @param item : une instance métier de la datasource
   * Va appeler innerDeleteSelectedItem
   */
  public deleteSelectedItem(item: T): void {
    // collecte du Tiw lié
    const tiw: TreeItemWrapper<T> = this._rawSourceMap.get(item[this.itemCodeField]);
    this.innerDeleteSelectedItem(tiw);
    this.innerDeleteSelectedToRoot(tiw);
  }

  /** suppression interne de selected, utilise TIW */
  private innerDeleteSelectedItem(tiw: TreeItemWrapper<T>): void {
    const selectedIdx: number = this._selectedItems.indexOf(tiw.source);
    if (selectedIdx >= 0) {
      this._selectedItems.splice(selectedIdx, 1); // déselection
    }
    if (tiw.children) {
      // si enfants, deselect
      tiw.children.forEach((susbTiw: TreeItemWrapper<T>) => {
        this.innerDeleteSelectedItem(susbTiw);
      });
    }
  }
  /** traitement ascendant : si je viens de supprimer un enfant, deselect du parent */
  private innerDeleteSelectedToRoot(tiw: TreeItemWrapper<T>): void {
    if (!WizStringUtils.isEmpty(tiw.parentCode)) {
      // si j'ai un parent
      const parentTiw: TreeItemWrapper<T> = this._rawSourceMap.get(tiw.parentCode);
      const selectedIdx: number = this._selectedItems.indexOf(parentTiw.source);
      if (selectedIdx >= 0) {
        this._selectedItems.splice(selectedIdx, 1); // déselection
      }
      this.innerDeleteSelectedToRoot(parentTiw); // contrôle récursif
    }
  }

  // ####
  // ####
  // #### add / remove / move items
  // ####
  // ####

  /**
   * teste si l'élément déplacé (dragged) est un ancêtre de la cible (Over) ou self
   * @param pDraggedItem le TIW en cours de drag
   * @param pOverItem l'élément survolé
   */
  public isAncestor(pDraggedItem: TreeItemWrapper<T>, pOverItem: TreeItemWrapper<T>): boolean {
    const result = false;
    let whosyourdaddy: string = pOverItem ? pOverItem.code : ''; // pour éviter self (self = true)
    while (!WizStringUtils.isEmpty(whosyourdaddy)) {
      if (whosyourdaddy === pDraggedItem.code) {
        return true;
      }
      // et on prend le papa du papa
      whosyourdaddy = this._rawSourceMap.get(whosyourdaddy).parentCode;
    }
    return result;
  }

  /**
   * Ajoute un ou des items à la datasource
   * Un refresh est effectué après ajout
   *
   * @param pValue La ou les valeurs à ajouter
   * @param pTarget optional L'index à partir duquel ajouter les éléments
   */
  public addChild(pValue: T, pTarget: TreeItemWrapper<T>): void {
    // TODO ajout des enfants ?

    // si pas de pTarget, ajout sur root
    pValue[this.itemParentCodeField] = pTarget ? pTarget.code : '';

    // contrôle unicité
    if (this._rawSourceMap.get(pValue[this.itemCodeField])) {
      // existe : Pas d'ajout. simple replace
      // accès à la source depuis TIW.source, changement du code parent
      this._rawSourceMap.get(pValue[this.itemCodeField]).source[this.itemParentCodeField] = pTarget.code;
    } else {
      this._source.push(pValue); // ajout véritable
    }
    this._needRebuild = true;
    this._refreshSubject.next();
  }

  private recurseRemove(pTiwDelete: TreeItemWrapper<T>): void {
    if (pTiwDelete.children) {
      pTiwDelete.children.forEach((child: TreeItemWrapper<T>) => {
        this.recurseRemove(child); // même traitement pour les enfants
      });
    }
    this._source.splice(this._source.indexOf(pTiwDelete.source), 1); // et suppression self
  }

  /**
   * suppression d'une branche et de ses enfants
   * Un refresh est effectué après ajout
   * @param pDelete l'objet métier source à supprimer
   */
  public removeChild(pDelete: T): void {
    // on identifie le TIW correspondant dans le _rawSourceMap
    const tiwDelete: TreeItemWrapper<T> = this._rawSourceMap.get(pDelete[this.itemCodeField]);
    this.recurseRemove(tiwDelete);

    // reconstruction du tree
    this._needRebuild = true;

    this._refreshSubject.next();
  }

  /**
   * ajout d'un ou plusieurs éléments à la sélection en cours
   * @param pItems Les items à sélectionner
   */
  public addSelectedItems(pItems: T[]): void {
    if (pItems) {
      pItems.forEach((item: T) => {
        if (!this.isSelected(item)) {
          // pas d'ajout si déjà présent
          this._selectedItems.push(item);
        }
      });
    }
    this._changes.next({
      type: 'programmaticChanged',
      value: this._selectedItems,
    });
  }

  /**
   * ajout d'un ou plusieurs éléments à la sélection en cours
   * @param pItems Les items à sélectionner
   */
  public removeSelectedItems(pItems: T[]): void {
    if (pItems) {
      pItems.forEach((item: T) => {
        if (this.isSelected(item)) {
          this._selectedItems.splice(this._selectedItems.indexOf(item), 1);
        }
      });
    }
    this._changes.next({
      type: 'programmaticChanged',
      value: this._selectedItems,
    });
  }
}
